import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { get, intersectionWith } from 'lodash';

import { ENTITY_TYPE, FIELD_PATHS, FIELD_VALUES, URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { useCustomer } from '@funnels/hooks';
import {
  FunnelConfig,
  PostProductSelectContract,
  ReduxStoreData,
  StoreApplicant,
  StoreEmployment,
  UseParams,
} from '@funnels/types';
import { getDashboardUrl, getIndexFromCustomerId } from '@funnels/utils';

import { PostProductSelectForm } from '../PostProductSelectForm';
import { ACCOUNTANTS_BASE_NAME, APPLICANT_BASE_NAME, BASE_NAME, PROPERTIES_BASE_NAME } from './constants';
import {
  getAccountantQuestionConfig,
  getAccountantSelects,
  getAccountDetailsQuestionConfig,
  getApplicantQuestionsConfig,
  getApplicantSelects,
  getAssetAccountSelects,
  getConveyancerQuestionConfig,
  getConveyancerSelects,
  getLicensedBuilderQuestionsConfig,
  getNextPPSRoute,
  getPropertyQuestionsConfig,
  getPropertySelects,
} from './utils';

function mapState(currentState: ReduxStoreData) {
  const data = {
    applicants: currentState.applicants.data,
    employments: currentState.employments.data,
    postProductSelect: currentState.postProductSelect.data,
  };

  return { data };
}

export const DynamicRouter = () => {
  const { data } = useSelector(mapState);
  const { [URL_PARAM_APPLICATION_ID]: applicationId = '' } = useParams<UseParams>();
  const customer = useCustomer();
  const { questionConfigs, firstApplicantId, applicantsWithSelfEmploymentStatus } = useMemo(() => {
    const postProductSelects: PostProductSelectContract[] = get(data, FIELD_PATHS.POST_PRODUCT_SELECTS(), []);
    const customerApplicantIndex = getIndexFromCustomerId(get(data, FIELD_PATHS.APPLICANTS(), []), customer.data.id);
    const customerRole = get(data, FIELD_PATHS.APPLICANT_TYPE(customerApplicantIndex), FIELD_VALUES.BORROWER);
    const applicants: StoreApplicant[] = get(data, FIELD_PATHS.APPLICANTS(), []).filter(
      (applicant: StoreApplicant) =>
        customerRole === get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0), FIELD_VALUES.BORROWER) ||
        customerRole === FIELD_VALUES.BORROWER
    );
    const employments: StoreEmployment[] = get(data, FIELD_PATHS.EMPLOYMENTS(), []);

    const applicantSelects = getApplicantSelects(applicants, postProductSelects);
    const propertySelects = getPropertySelects(postProductSelects);
    const conveyancerSelects = getConveyancerSelects(postProductSelects);
    const accountantSelects = getAccountantSelects(postProductSelects);
    const assetAccountSelects = getAssetAccountSelects(postProductSelects);

    const licensedBuilderSelects: PostProductSelectContract[] = postProductSelects.filter(
      (item: PostProductSelectContract) =>
        get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) ===
        ENTITY_TYPE.LICENSED_BUILDER
    );

    const selfEmployments = intersectionWith(
      employments,
      accountantSelects,
      (employment, accountantSelect) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_ID(0)) ===
        get({ postProductSelect: [accountantSelect] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0))
    );

    const liabilityAccountSelects: PostProductSelectContract[] = postProductSelects.filter(
      (item: PostProductSelectContract) =>
        get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) ===
        ENTITY_TYPE.LIABILITY_ACCOUNT
    );

    const liabilitySelects: PostProductSelectContract[] = postProductSelects.filter(
      (item: PostProductSelectContract) =>
        get({ postProductSelect: [item] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.LIABILITY
    );

    // ```applicantsWithSelfEmploymentStatus``` is a collection of applicant id and self employed boolean, e.g:
    // [
    //   {
    //     applicantId: 'applicant-1',
    //     selfEmployed: true,
    //   },
    //   {
    //     applicantId: 'applicant-2',
    //     selfEmployed: false,
    //   },
    //   {
    //     applicantId: 'applicant-3',
    //     selfEmployed: true,
    //   },
    // ]
    // It will be used for dynamic routing to determine whether ```/post-product-select/accountant/:applicant-id```
    // should be generated based on if this applicant has self-employment. Its interface is declared in ./utils.ts
    const applicantsWithSelfEmploymentStatus = applicants.map((applicant) => ({
      id: get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0)),
      selfEmployed: selfEmployments.some(
        (employment) =>
          get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) ===
          get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ID(0))
      ),
    }));

    const firstApplicantId = get({ postProductSelect: applicantSelects }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0));

    const applicantQuestionConfigs = getApplicantQuestionsConfig(
      applicantSelects,
      getNextPPSRoute(
        applicationId,
        {
          applicantSelects,
          propertySelects,
          accountantSelects,
          conveyancerSelects,
          licensedBuilderSelects,
          assetAccountSelects,
          liabilityAccountSelects,
          liabilitySelects,
        },
        ENTITY_TYPE.ACCOUNTANT
      )
    );

    const propertyQuestionConfigs = getPropertyQuestionsConfig(
      propertySelects,
      getNextPPSRoute(
        applicationId,
        {
          applicantSelects,
          propertySelects,
          accountantSelects,
          conveyancerSelects,
          licensedBuilderSelects,
          assetAccountSelects,
          liabilityAccountSelects,
          liabilitySelects,
        },
        ENTITY_TYPE.LICENSED_BUILDER,
        undefined,
        applicantsWithSelfEmploymentStatus
      )
    );

    const licensedBuilderConfigs = getLicensedBuilderQuestionsConfig(
      licensedBuilderSelects,
      getNextPPSRoute(
        applicationId,
        {
          applicantSelects,
          propertySelects,
          accountantSelects,
          conveyancerSelects,
          licensedBuilderSelects,
          assetAccountSelects,
          liabilityAccountSelects,
          liabilitySelects,
        },
        ENTITY_TYPE.SOLICITOR
      )
    );

    const accountantQuestionConfigs = getAccountantQuestionConfig(
      applicantsWithSelfEmploymentStatus,
      accountantSelects,
      getNextPPSRoute(
        applicationId,
        {
          applicantSelects,
          propertySelects,
          accountantSelects,
          conveyancerSelects,
          licensedBuilderSelects,
          assetAccountSelects,
          liabilityAccountSelects,
          liabilitySelects,
        },
        ENTITY_TYPE.ASSET
      )
    );

    const conveyancerQuestionConfigs = getConveyancerQuestionConfig(
      conveyancerSelects,
      getNextPPSRoute(
        applicationId,
        {
          applicantSelects,
          propertySelects,
          accountantSelects,
          conveyancerSelects,
          licensedBuilderSelects,
          assetAccountSelects,
          liabilityAccountSelects,
          liabilitySelects,
        },
        ENTITY_TYPE.ASSET_ACCOUNT
      )
    );

    const accountDetailsQuestionConfigs = getAccountDetailsQuestionConfig(
      assetAccountSelects,
      liabilityAccountSelects,
      liabilitySelects,
      {
        url: getDashboardUrl(applicationId),
        hardRedirect: true,
      }
    );

    return {
      firstApplicantId,
      questionConfigs: [
        ...applicantQuestionConfigs,
        ...accountantQuestionConfigs,
        ...propertyQuestionConfigs,
        ...licensedBuilderConfigs,
        ...conveyancerQuestionConfigs,
        ...accountDetailsQuestionConfigs,
      ],
      applicantsWithSelfEmploymentStatus,
    };
  }, [data, applicationId]);

  if (!questionConfigs.length) {
    window.location.assign(getDashboardUrl(applicationId));

    // prevent the router from re-entering while the browser commences redirect (async)
    return null;
  }

  const dynamicConfig: FunnelConfig = {
    baseName: BASE_NAME,
    entryPoint: (values: any, LDFlags: LDFlagSet | undefined, params: UseParams | undefined = {}) => {
      const firstApplicantIdWithSelfEmployment = applicantsWithSelfEmploymentStatus.find((a) => a.selfEmployed)?.id;
      if (firstApplicantId) {
        return `${APPLICANT_BASE_NAME}${firstApplicantId}`;
      } else if (firstApplicantIdWithSelfEmployment) {
        return `${ACCOUNTANTS_BASE_NAME}${firstApplicantIdWithSelfEmployment}`;
      }
      return `${PROPERTIES_BASE_NAME}`;
    },
    questions: questionConfigs,
  };

  return <PostProductSelectForm config={dynamicConfig} />;
};
